import moment, { Moment } from 'moment';
import { useMemo, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import {
  PackagesFormValues,
  PackagesGuest,
} from '@common/features/DynamicPackagesConfigurationWizardNew/types';
import { ExperiencesByDay, DateGroupData } from '../types';
import { flattenExperiences } from './flattenExperiences';

export interface UseDateGroupSelectionProps {
  selectedExperiencesByDay: ExperiencesByDay[];
  journeyParams: {
    fromDate: string | Moment;
  };
  guests: PackagesGuest[];
  setValue: UseFormSetValue<PackagesFormValues>;
  spaAvailableDates?: string[];
}

interface UseDateGroupSelectionReturn {
  dateGroupData: DateGroupData[];
  handleDayChange: (groupKey: string, selectedDateString: string) => void;
}

/**
 * Handles day selection for repeating spa experience groups that can be scheduled on different days.
 *
 * How it works:
 * - Each spa experience group gets repeated and needs its own day
 * - Once a day is used by one group, other groups can't use that same day
 * - If a group only has one available day left (after others picked), it gets auto-selected
 * - When you pick a day, all guests' bookings for that group are updated
 *
 * Example: You have two spa experience groups, each available on Day 1, 2, or 3.
 * If you schedule the first group for Day 2, the second group can only use Day 1 or 3.
 * This prevents scheduling multiple groups on the same day.
 */
export const useDateGroupSelection = ({
  selectedExperiencesByDay,
  journeyParams,
  guests,
  setValue,
  spaAvailableDates = [],
}: UseDateGroupSelectionProps): UseDateGroupSelectionReturn => {
  const [selectedDayByGroup, setSelectedDayByGroup] = useState<Record<string, number>>({});
  const fromDate = moment(journeyParams.fromDate);

  const flattenedExperiences = useMemo(
    () => flattenExperiences(selectedExperiencesByDay),
    [selectedExperiencesByDay]
  );

  const dateGroupData = useMemo(() => {
    return selectedExperiencesByDay.map((dayGroup, groupIndex) => {
      const { date, dateRange } = dayGroup;
      const groupKey = date;
      const selectedDay = selectedDayByGroup[groupKey];

      const selectedDaysInPreviousGroups = new Set<number>();
      selectedExperiencesByDay.slice(0, groupIndex).forEach((prevGroup) => {
        const prevSelectedDay = selectedDayByGroup[prevGroup.date];
        if (prevSelectedDay !== undefined) {
          selectedDaysInPreviousGroups.add(prevSelectedDay);
        }
      });

      const availableDates = dateRange
        .filter((dateStr) => {
          const parsedDate = moment(dateStr);
          const dayNum = parsedDate.diff(fromDate, 'days');
          const isNotSelectedByPreviousGroup = !selectedDaysInPreviousGroups.has(dayNum);
          const formattedDate = parsedDate.format('YYYY-MM-DD');
          const hasSpaAvailability =
            spaAvailableDates.length === 0 || spaAvailableDates.includes(formattedDate);
          return isNotSelectedByPreviousGroup && hasSpaAvailability;
        })
        .map((dateStr) => moment(dateStr).format('YYYY-MM-DD'));

      let autoSelectedDay: number | undefined;
      if (availableDates.length === 1 && selectedDay === undefined) {
        autoSelectedDay = moment(availableDates[0]).diff(fromDate, 'days');
      }

      const experiencesInGroup = flattenedExperiences.filter((exp) => exp.date === date);

      return {
        date,
        dateRange,
        groupKey,
        selectedDay: autoSelectedDay ?? selectedDay,
        availableDates,
        experiences: experiencesInGroup,
        isConflicting: selectedDay !== undefined && selectedDaysInPreviousGroups.has(selectedDay),
      };
    });
  }, [selectedExperiencesByDay, flattenedExperiences, selectedDayByGroup, fromDate]);

  useMemo(() => {
    const updates: Record<string, number | undefined> = {};
    let hasChanges = false;

    dateGroupData.forEach(({ groupKey, selectedDay, isConflicting }) => {
      const currentSelection = selectedDayByGroup[groupKey];

      if (isConflicting && currentSelection !== undefined) {
        updates[groupKey] = undefined;
        hasChanges = true;
      } else if (selectedDay !== undefined && currentSelection !== selectedDay) {
        updates[groupKey] = selectedDay;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setSelectedDayByGroup((prev) => {
        const next = { ...prev };
        Object.entries(updates).forEach(([key, value]) => {
          if (value === undefined) {
            delete next[key];
          } else {
            next[key] = value;
          }
        });
        return next;
      });
    }
  }, [dateGroupData, selectedDayByGroup]);

  const handleDayChange = (groupKey: string, selectedDateString: string) => {
    const dayNum = moment(selectedDateString).diff(fromDate, 'days');
    setSelectedDayByGroup((prev) => ({ ...prev, [groupKey]: dayNum }));

    const group = dateGroupData.find((g) => g.groupKey === groupKey);
    if (group) {
      group.experiences.forEach(({ experienceIndex }) => {
        guests.forEach((_, guestIndex) => {
          setValue(
            `guests.${guestIndex}.experiences.${experienceIndex}`,
            {
              dayNumber: dayNum,
              startTime: undefined,
              endTime: undefined,
              visitDate: undefined,
            } as any,
            { shouldValidate: false }
          );
        });
      });
    }
  };

  return {
    dateGroupData,
    handleDayChange,
  };
};
